import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress, useMediaQuery, useTheme } from '@mui/material';

/**
 * Here's official example of an embed code:
 * 
 * <blockquote class="tiktok-embed" cite="https://www.tiktok.com/@buildandcreatehome/video/7259063768955538731" data-video-id="7259063768955538731" style="max-width: 605px;min-width: 325px;" > <section> <a target="_blank" title="@buildandcreatehome" href="https://www.tiktok.com/@buildandcreatehome?refer=embed">@buildandcreatehome</a> $85 in lumber and still my most popular build yet! Super easy for beginner builders. <a title="diyproject" target="_blank" href="https://www.tiktok.com/tag/diyproject?refer=embed">#diyproject</a> <a title="diyhome" target="_blank" href="https://www.tiktok.com/tag/diyhome?refer=embed">#diyhome</a> <a title="homedecor" target="_blank" href="https://www.tiktok.com/tag/homedecor?refer=embed">#homedecor</a> <a title="shelfie" target="_blank" href="https://www.tiktok.com/tag/shelfie?refer=embed">#shelfie</a> <a title="shelvesdecor" target="_blank" href="https://www.tiktok.com/tag/shelvesdecor?refer=embed">#shelvesdecor</a> <a target="_blank" title="♬ Powerful Women - Pitbull &#38; Dolly Parton" href="https://www.tiktok.com/music/Powerful-Women-7333994255633942529?refer=embed">♬ Powerful Women - Pitbull &#38; Dolly Parton</a> </section> </blockquote> <script async src="https://www.tiktok.com/embed.js"></script>
 */

interface Props {
    videoId: string;
}

const TikTokEmbed = ({ videoId }: Props) => {
	const theme = useTheme();
	const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	// Function to load TikTok script
	const loadTikTokScript = () => {
		return new Promise<void>((resolve, reject) => {
			// Remove any existing script
			const existingScript = document.querySelector('script[src="https://www.tiktok.com/embed.js"]');
			if (existingScript) {
				existingScript.remove();
			}

			const script = document.createElement('script');
			script.src = 'https://www.tiktok.com/embed.js';
			script.async = true;
			script.onload = () => {
				// Give TikTok's script a moment to initialize
				setTimeout(() => {
					resolve();
				}, 100);
			};
			script.onerror = () => reject(new Error('Failed to load TikTok script'));
			document.body.appendChild(script);
		});
	};

	useEffect(() => {
		let mounted = true;

		const initializeEmbed = async () => {
			setIsLoading(true);
			setError(null);

			try {
				await loadTikTokScript();
				if (mounted) {
					setIsLoading(false);
				}
			} catch (err) {
				if (mounted) {
					setError('Failed to load TikTok embed');
					setIsLoading(false);
				}
			}
		};

		initializeEmbed();

		return () => {
			mounted = false;
		};
	}, [videoId]);

	// Create the TikTok embed HTML using official format
	const tiktokEmbedHtml = `
		<blockquote 
			class="tiktok-embed" 
			cite="https://www.tiktok.com/@user/video/${videoId}" 
			data-video-id="${videoId}" 
			// style="max-width: ${isSmUp ? 450 : 345}px; min-width: 325px;"
		>
			<section>
				<a target="_blank" title="TikTok Video" href="https://www.tiktok.com/@user/video/${videoId}?refer=embed">
					View this TikTok video
				</a>
			</section>
		</blockquote>
	`;

	return (
		<div style={{ minHeight: '450px', position: 'relative' }}>
			{isLoading && (
				<Box 
					display="flex" 
					flexDirection="column" 
					alignItems="center" 
					gap={2}
					position="absolute"
					top="50%"
					left="50%"
					sx={{ transform: 'translate(-50%, -50%)' }}
				>
					<CircularProgress />
					<Typography variant="body2" color="text.secondary">
						Loading TikTok content...
					</Typography>
				</Box>
			)}
			
			{error && (
				<Typography 
					color="error" 
					align="center"
					position="absolute"
					top="50%"
					left="50%"
					sx={{ transform: 'translate(-50%, -50%)' }}
				>
					{error}
				</Typography>
			)}
			
			<div
				dangerouslySetInnerHTML={{ __html: tiktokEmbedHtml }}
				style={{ 
					opacity: isLoading ? 0 : 1,
					transition: 'opacity 0.3s ease-in-out'
				}}
			/>
		</div>
	);
};

export default TikTokEmbed; 